import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/utils/api'
import { getTodayDateString } from '@/utils/timezoneUtils'
import { format, parseISO, addDays } from 'date-fns'
import {
  GraduationCap, Bus, Calendar, Clock, AlertCircle,
  MapPin, ChevronRight, Sun, Sunset, Phone, Globe,
  ExternalLink, Navigation, Bell
} from 'lucide-react'

interface SchoolInfo {
  user: {
    username: string
    display_name: string
    transport_mode?: string
    bus_number?: string
  }
  school: {
    id?: string
    name?: string
    address?: string
    phone?: string
    website?: string
    district?: string
    calendar_url?: string
  }
  schedule: {
    school_start?: string
    school_end?: string
    bus_pickup?: string
    bus_dropoff?: string
    car_pickup?: string
    car_dropoff?: string
  }
}

interface SchoolEvent {
  id: string
  school: string
  name: string
  date: string
  type: string
  description?: string
  early_release?: string
  location?: string
  game_type?: string
}

interface TransportScheduleEntry {
  date: string
  morning_method?: string
  morning_time?: string
  morning_notes?: string
  afternoon_method?: string
  afternoon_time?: string
  afternoon_notes?: string
  is_override: boolean
  reason?: string
  at_parent1_house?: boolean
}

export const SchoolPage: React.FC = () => {
  const { user } = useAuth()
  const { isFeatureEnabled } = useFamilyFeatures()
  const navigate = useNavigate()
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | null>(null)
  const [events, setEvents] = useState<SchoolEvent[]>([])
  const [transportSchedule, setTransportSchedule] = useState<TransportScheduleEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [todayStr, setTodayStr] = useState('')

  // Redirect if school feature is not enabled
  useEffect(() => {
    if (!isFeatureEnabled('school')) {
      navigate('/dashboard/home', { replace: true })
    }
  }, [isFeatureEnabled, navigate])

  useEffect(() => {
    const init = async () => {
      const today = await getTodayDateString()
      setTodayStr(today)
      fetchData(today)
    }
    init()
  }, [user?.id])

  const fetchData = async (today: string) => {
    if (!user?.id) return

    try {
      setLoading(true)

      // Calculate date range for transport schedule (next 7 days)
      const endDate = format(addDays(parseISO(today), 7), 'yyyy-MM-dd')

      const [schoolInfoRes, eventsRes, transportRes] = await Promise.all([
        apiClient.get(`/api/school-transport/school-info/${user.id}`).catch(() => null),
        apiClient.get(`/api/school/events?user_id=${user.id}&days_ahead=60`).catch(() => []),
        apiClient.get(`/api/school-transport/transport/schedule/${user.id}?date_from=${today}&date_to=${endDate}`).catch(() => [])
      ])

      if (schoolInfoRes) {
        setSchoolInfo(schoolInfoRes)
      }

      // Process events
      const allEvents = Array.isArray(eventsRes) ? eventsRes : (eventsRes?.data || [])
      setEvents(allEvents.slice(0, 10))

      // Process transport schedule
      const schedule = Array.isArray(transportRes) ? transportRes : (transportRes?.data || [])
      setTransportSchedule(schedule)

    } catch (error) {
      console.error('Error fetching school data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getMethodLabel = (method?: string) => {
    switch (method) {
      case 'bus': return 'Bus'
      case 'parent_pickup': case 'parent_car': case 'car': return 'Parent Pickup'
      case 'carpool': return 'Carpool'
      case 'drives_self': return 'Drive Self'
      case 'walk': return 'Walk'
      case 'bike': return 'Bike'
      default: return method || 'TBD'
    }
  }

  const getMethodIcon = (method?: string) => {
    switch (method) {
      case 'bus': return Bus
      case 'parent_pickup': case 'parent_car': case 'car': return MapPin
      case 'walk': case 'bike': return Navigation
      default: return Bus
    }
  }

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'no_school': return 'bg-red-100 text-red-800 border-red-200'
      case 'early_release': return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'late_start': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'holiday': return 'bg-green-100 text-green-800 border-green-200'
      case 'test': case 'testing': return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'sports': case 'game': return 'bg-orange-100 text-orange-800 border-orange-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'no_school': return 'No School'
      case 'early_release': return 'Early Release'
      case 'late_start': return 'Late Start'
      case 'holiday': return 'Holiday'
      case 'test': case 'testing': return 'Testing'
      case 'sports': case 'game': return 'Sports'
      default: return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  const getTodaySchedule = () => {
    return transportSchedule.find(s => s.date === todayStr)
  }

  const todaySchedule = getTodaySchedule()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading school info...</p>
        </div>
      </div>
    )
  }

  const school = schoolInfo?.school
  const schedule = schoolInfo?.schedule

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* School Info Header */}
      <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-10 h-10" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{school?.name || 'No School Assigned'}</h1>
            <p className="text-blue-100">{school?.district || ''}</p>
            {school?.address && (
              <p className="text-blue-100 text-sm mt-1 flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {school.address}
              </p>
            )}
          </div>
        </div>

        {/* Contact & Quick Links */}
        {(school?.phone || school?.website) && (
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-white/20">
            {school.phone && (
              <a
                href={`tel:${school.phone}`}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm">{school.phone}</span>
              </a>
            )}
            {school.website && (
              <a
                href={school.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm">Website</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* Bell Schedule & Transport Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Bell Schedule */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Bell Schedule
            </h2>
          </div>
          <div className="p-5">
            {(schedule?.school_start || schedule?.school_end) ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-3">
                    <Sun className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-900">First Bell</span>
                  </div>
                  <span className="text-lg font-bold text-amber-700">{schedule.school_start || '--:--'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-3">
                    <Sunset className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-900">Dismissal</span>
                  </div>
                  <span className="text-lg font-bold text-purple-700">{schedule.school_end || '--:--'}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No bell schedule set</p>
              </div>
            )}
          </div>
        </div>

        {/* Today's Transportation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 text-white">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Bus className="w-5 h-5" />
              Today's Transport
            </h2>
          </div>
          <div className="p-5">
            {todaySchedule ? (
              <div className="space-y-4">
                {/* Morning */}
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-3">
                    <Sun className="w-5 h-5 text-amber-600" />
                    <div>
                      <span className="font-medium text-amber-900">Morning</span>
                      {todaySchedule.morning_time && (
                        <p className="text-xs text-amber-700">{todaySchedule.morning_time}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-amber-700">
                    {React.createElement(getMethodIcon(todaySchedule.morning_method), { className: 'w-5 h-5' })}
                    <span className="font-semibold">{getMethodLabel(todaySchedule.morning_method)}</span>
                  </div>
                </div>

                {/* Afternoon */}
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-3">
                    <Sunset className="w-5 h-5 text-purple-600" />
                    <div>
                      <span className="font-medium text-purple-900">Afternoon</span>
                      {todaySchedule.afternoon_time && (
                        <p className="text-xs text-purple-700">{todaySchedule.afternoon_time}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-purple-700">
                    {React.createElement(getMethodIcon(todaySchedule.afternoon_method), { className: 'w-5 h-5' })}
                    <span className="font-semibold">{getMethodLabel(todaySchedule.afternoon_method)}</span>
                  </div>
                </div>

                {/* Bus number if applicable */}
                {schoolInfo?.user.bus_number && (
                  <div className="text-center text-sm text-gray-600">
                    Bus #{schoolInfo.user.bus_number}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Bus className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No transport today</p>
                <p className="text-xs">(Weekend or holiday)</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Weekly Transport Schedule */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            This Week's Transportation
          </h2>
        </div>
        <div className="p-4">
          {transportSchedule.length > 0 ? (
            <div className="space-y-2">
              {transportSchedule.map(entry => {
                const isToday = entry.date === todayStr
                const dayName = format(parseISO(entry.date), 'EEEE')
                const dayNum = format(parseISO(entry.date), 'MMM d')
                const isWeekend = dayName === 'Saturday' || dayName === 'Sunday'

                if (isWeekend) return null

                return (
                  <div
                    key={entry.date}
                    className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      isToday
                        ? 'bg-blue-50 border-2 border-blue-300'
                        : entry.is_override
                          ? 'bg-orange-50 border border-orange-200'
                          : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`text-sm font-medium w-20 ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                        {dayName.slice(0, 3)}
                        <span className="text-xs text-gray-500 ml-1">{dayNum}</span>
                      </div>
                      {isToday && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">Today</span>
                      )}
                      {entry.is_override && (
                        <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">Changed</span>
                      )}
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2 text-amber-700 min-w-[100px]">
                        <Sun className="w-4 h-4" />
                        <span>{getMethodLabel(entry.morning_method)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-purple-700 min-w-[100px]">
                        <Sunset className="w-4 h-4" />
                        <span>{getMethodLabel(entry.afternoon_method)}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No transport schedule available</p>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Events */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-4 text-white">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Upcoming School Events
          </h2>
        </div>
        <div className="p-4">
          {events.length > 0 ? (
            <div className="space-y-3">
              {events.map(event => (
                <div
                  key={event.id}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="text-center min-w-[60px] bg-white rounded-lg p-2 shadow-sm">
                    <div className="text-2xl font-bold text-gray-900">
                      {format(parseISO(event.date), 'd')}
                    </div>
                    <div className="text-xs text-gray-500 uppercase">
                      {format(parseISO(event.date), 'MMM')}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-1 rounded-full border ${getEventTypeColor(event.type)}`}>
                        {getEventTypeLabel(event.type)}
                      </span>
                      {event.game_type && (
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                          {event.game_type}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mt-1">{event.name}</h3>
                    {event.description && (
                      <p className="text-sm text-gray-600 mt-0.5">{event.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      {event.early_release && (
                        <span className="flex items-center gap-1 text-amber-600">
                          <Clock className="w-3 h-3" />
                          {event.early_release}
                        </span>
                      )}
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No upcoming school events</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SchoolPage
